import { getProductId, getProductName } from "../utils";

export const formatNaira = (value) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

// Mirrors calculateTotalAmount in AppContext: discounted price wins, then the
// product price, then the box amount. Coerced so "500" and 500 compare equal.
// Note getProductPrice in ../utils ignores discountPrice, so it can't be used here.
export const getEffectiveUnitPrice = (item) =>
  Number(
    (item?.discountPrice ? item?.discountPrice : item?.price) || item?.amount,
  ) || 0;

// Re-prices a cart against the authoritative records from /product/compare and
// reports what moved. Items the server didn't price are left untouched.
//
// The compare response is the authority on discounts: it returns discountPrice
// only while a discount is actually live. No discountPrice on the response means
// the promo has ended, so it is cleared and the item falls back to full price —
// unlike GET /product, which keeps returning a stored discountPrice that may no
// longer apply.
export const reconcileCartPrices = (
  items = [],
  freshProducts = [],
  freshBoxes = [],
) => {
  const priceChanges = [];

  const repricedCart = items.map((item) => {
    const oldPrice = getEffectiveUnitPrice(item);

    if (item?.productName) {
      const product = freshProducts?.find(
        (p) => getProductId(p) === getProductId(item),
      );
      if (product?.price === undefined || product?.price === null) return item;

      const updatedItem = {
        ...item,
        price: product.price,
        discountPrice: product?.discountPrice || "",
        discountName: product?.discountName || "",
      };
      const newPrice = getEffectiveUnitPrice(updatedItem);
      if (newPrice === oldPrice) return item;

      priceChanges.push({ name: getProductName(item), oldPrice, newPrice });
      return updatedItem;
    }

    if (item?.name) {
      const box = freshBoxes?.find(
        (b) => getProductId(b) === getProductId(item),
      );
      if (box?.amount === undefined || box?.amount === null) return item;

      const updatedItem = { ...item, price: box.amount, amount: box.amount };
      const newPrice = getEffectiveUnitPrice(updatedItem);
      if (newPrice === oldPrice) return item;

      priceChanges.push({ name: getProductName(item), oldPrice, newPrice });
      return updatedItem;
    }

    return item;
  });

  return { repricedCart, priceChanges };
};

// A single change is worth naming; listing several makes an unreadable wall of
// text in a 3-second toast, so they collapse into one line and the customer
// reads the detail off the cart itself.
export const getPriceChangeMessage = (priceChanges = []) => {
  const action = "Please review your total and check out again.";

  if (priceChanges.length === 1) {
    const { name, oldPrice, newPrice } = priceChanges[0];
    return `${name} is now ${formatNaira(newPrice)} (was ${formatNaira(
      oldPrice,
    )}). ${action}`;
  }

  return `Prices in your cart have been updated. ${action}`;
};

// Splits a cart into the id lists /product/compare expects.
export const getCompareIds = (items = []) => ({
  productIds: items
    .filter((item) => item?.productName)
    .map((item) => getProductId(item)),
  boxIds: items.filter((item) => item?.name).map((item) => getProductId(item)),
});

// The endpoint has returned both a { products, boxes } object and a bare array
// over time, so normalise before reconciling.
export const normalizeCompareResult = (compareResult) => ({
  products:
    compareResult?.products ||
    (Array.isArray(compareResult) ? compareResult : []),
  boxes: compareResult?.boxes || [],
});
